import { appendScript, appendStylesheet, blockUntilEvent, createMicrowebTemplate, isCustomElementRegistered, } from '@helsenorge/microweb/utils';
import { getCookieValue, hasCookie } from '../cookie';
import { getAssetsUrl } from '../hn-page';
import { error } from '../logger';
const isTemplateRenderedServerSide = (templateElement) => {
    if (templateElement && templateElement.content.children.length > 0) {
        return true;
    }
    return false;
};
/**
 * Finn det første link-elementet i document.head som inneholder helsenorge.css
 * @returns URL til helsenorge.css i document.head, med hash i URLen
 */
const getHelsenorgeCssUrl = () => {
    const elementList = document.head.querySelectorAll(`link[href^="${getAssetsUrl()}/core/static/css/helsenorge."]`);
    const linkElement = Array.from(elementList)[0];
    return linkElement?.href;
};
const appendInlineScript = (contents) => {
    const scriptElement = document.createElement('script');
    scriptElement.innerHTML = contents;
    scriptElement.setAttribute('type', 'module');
    document.head.appendChild(scriptElement);
};
/**
 * Denne kan brukes for å fetche en web component som finnes.
 * Legger til <link> og <script> tags for relevant webcomponent så riktig js og css lastes, og oppretter element i dom'en.
 *
 * Tilsvarer "metode 2" for konsumering av web component som er dokumentert på confluence-siden for microfrontend:
 * https://confluence.helsedirektoratet.no/display/HR2/@helsenorge+Microfrontend
 *
 * Eksempel:
 * // Lag template én gang, i html'en eller på root. Fetch nødvendige assets med fetchWebComp
 * <template id="hn-webcomp-oppgave-template"></template>
 * // fetch assets, vanligvis i en UseEffect
 * fetchWebComp({domain={`${getAssetsUrl()}/oppgave`} entryName="webcomp-oppgave" componentName="hn-webcomp-oppgave" includeHelsenorgeCss});
 * // Lag flere instans av følgende i din react kode:
 * <hn-webcomp-oppgave></hn-webcomp-oppgave>
 */
export const fetchWebComp = async (config) => {
    // Venter med å sjekke om template er allerede lagt til server-side inntil HTML-en er innlastet.
    // Dette for å unngå undøvendig fetch av assets.json pga. race condition.
    if (document.readyState === 'loading') {
        await blockUntilEvent(document, 'DOMContentLoaded');
    }
    const { domain, componentName, entryName, includeHelsenorgeCss, templateName } = config;
    const microwebTemplate = document.getElementById(templateName || `${componentName}-template`);
    // Alt 1: Lokal utvikling med cookie
    const cookieName = componentName.replace('-webcomp-', '-assets-');
    const cookieValue = getCookieValue(cookieName);
    if (import.meta.env.DEV &&
        hasCookie(cookieName) &&
        !isCustomElementRegistered(componentName) &&
        !isTemplateRenderedServerSide(microwebTemplate)) {
        const templateElement = microwebTemplate ?? createMicrowebTemplate(templateName || `${componentName}-template`);
        if (includeHelsenorgeCss) {
            const helsenorgeCssUrl = getHelsenorgeCssUrl();
            appendStylesheet(helsenorgeCssUrl || `${getAssetsUrl()}/core/static/css/helsenorge/helsenorge.css`, templateElement);
        }
        appendInlineScript(`import { injectIntoGlobalHook } from "${cookieValue}/@react-refresh";
injectIntoGlobalHook(window);`);
        appendScript(`${cookieValue}/@vite/client`);
        appendInlineScript(`import '@helsenorge/core-build/web-component-helper';`);
        appendScript(`${cookieValue}/${entryName}`);
        return Promise.resolve();
    }
    // Alt 2: Produksjon
    if (!isCustomElementRegistered(componentName) && !isTemplateRenderedServerSide(microwebTemplate)) {
        const assetsUrl = `${domain}/assets.json`;
        // Sørg for å alltid hente nyeste assets.json
        // https://developer.mozilla.org/en-US/docs/Web/API/Request/cache
        return fetch(assetsUrl, { cache: 'no-cache' })
            .then(response => {
            if (response.status === 200) {
                const resp = response.json();
                return resp;
            }
            return Promise.reject(Error(`Error while fetching asset-manifest.json from ${domain}/assets.json`));
        })
            .then((assets) => {
            const templateElement = microwebTemplate ?? createMicrowebTemplate(templateName || `${componentName}-template`);
            if (includeHelsenorgeCss) {
                const helsenorgeCssUrl = getHelsenorgeCssUrl();
                appendStylesheet(helsenorgeCssUrl || `${getAssetsUrl()}/core/static/css/helsenorge/helsenorge.css`, templateElement);
            }
            assets[entryName]?.css?.forEach(asset => {
                appendStylesheet(`${domain}/${asset}`, templateElement);
            });
            if (assets[entryName]) {
                appendScript(`${domain}/${assets[entryName]?.file}`);
            }
        })
            .catch(e => {
            error(e, assetsUrl);
        });
    }
    return Promise.resolve();
};
